

import { Event } from "@/types/Event";
import { EventSwitcher } from "../../Switcher/event-switcher/EventSwitcher";
import { User } from "@/types/User";
import { UserLog } from "@/types/UserLog";
import { AppBarUserControls } from "./AppBarUserControls";
import { Logo } from "./Logo";
import { useEffect } from "react";
import { getJSONPref, setJSONPref } from "@/lib/storage/userPrefs";
import { useTranslations } from "next-intl";
import { OnboardingPill } from "../../POnboarding/OnboardingPill";
import { useSidebar } from "@/providers/SidebarProvider";
import { IconMenu2 } from "@tabler/icons-react";

type AppBarContentProps = {
  events: {
    validEvents: Event[];
    validPinnedEvents: Event[];
    validRecentEvents: Event[];
  };
  user: User;
  notifications: UserLog[];
  notificationsUnread: UserLog[];
  numUnread: number;
  fetchDate: Date;
  event: Event | null;
};

export const AppBarContent: React.FC<AppBarContentProps> = ({
  events,
  user,
  notifications,
  notificationsUnread,
  numUnread,
  fetchDate,
  event,
}) => {
  const t = useTranslations("appbar");
  const { toggle } = useSidebar();

  useEffect(() => {
    const fetch = async () => {
      const eventId = event?.eventId ?? "";
      if (event) {
        let recentEventIds = getJSONPref<string[]>("recentEventIds", []);
        if (!Array.isArray(recentEventIds)) {
          recentEventIds = [];
        }
        recentEventIds = recentEventIds.filter((id) => id !== eventId);
        recentEventIds.unshift(eventId);
        if (recentEventIds?.length > 7) recentEventIds.pop();
        setJSONPref("recentEventIds", recentEventIds);
      }
    };

    fetch();
  }, [event]);

  return (
    <div className="flex flex-col">
      {/* Fixed h-12 (48px) with the border-b ON THIS ROW (not the wrapper):
          deterministic height AND the border lands on the same pixel as the AI
          assistant rail header (also `h-12 border-b`, border-box) so their lines
          line up exactly. Tallest child is ~32px (OnboardingPill), centered. */}
      <div className="flex flex-row items-center h-12 px-3 lg:pl-5 lg:pr-4 border-b outline-quaternary">
        {event && (
          <button
            className="lg:hidden p-2 -ml-1 mr-1 rounded-lg hover:surface-secondary transition-colors"
            onClick={toggle}
          >
            <IconMenu2 size={20} />
          </button>
        )}
        {/* Application logo component */}
        {event ? <div className="flex-1" /> : <Logo />}
        {/* Event selection dropdown component - hide when no events exist */}
        {(event ||
          events.validEvents.length > 0 ||
          events.validPinnedEvents.length > 0 ||
          events.validRecentEvents.length > 0) && (
          <EventSwitcher
            eventSelected={event ? true : false}
            initialItem={event ? (event.name ?? "") : t("eventNavigation")}
            eventsProp={events}
            eventId={event?.eventId}
          />
        )}
        {/* User controls including profile and notification buttons */}
        <AppBarUserControls
          user={user}
          notificationsProp={notifications}
          notificationsUnreadProp={notificationsUnread}
          numUnreadProp={numUnread}
          fetchDate={fetchDate}
        />
        <OnboardingPill />
      </div>
    </div>
  );
};
